// Owner-type filter for the map and list (#3205): the owner kinds wholesalers
// target first. Pure, so the rules are tested on their own.
//
// A property shows if its owner matches ANY checked type (same rule as the lead
// type checkboxes). Owner data is gated for free viewers and absent on some
// rows, so "can't tell" is its own outcome, kept while missing data is included
// (the map buy box's switch), never silently treated as "no match".

import { Property } from "../types";

export type OwnerType = "absentee" | "entity" | "out_of_state" | "estate";

export const OWNER_TYPES: { value: OwnerType; label: string; hint: string }[] = [
  { value: "absentee", label: "Absentee owner", hint: "Owner mails to a different address than the property" },
  { value: "entity", label: "LLC / company", hint: "Owned by a company, trust or other entity" },
  { value: "out_of_state", label: "Out of state", hint: "Owner's mailing address is outside NC" },
  { value: "estate", label: "Estate / heirs", hint: "Owner name reads as an estate, heirs or devisees" },
];

// Mirrors the backend's heir/estate reading of an owner name (#950) closely
// enough for a filter: the server stays the source of truth for the Leads tab.
const ESTATE_RE = /\b(estate of|heirs?\b|devisees?\b|unknown heirs|deceased)\b/i;

export function isEstateName(name: string | undefined): boolean {
  return !!name && ESTATE_RE.test(name);
}

type Verdict = "yes" | "no" | "unknown";

function verdictFor(type: OwnerType, p: Property): Verdict {
  const o = p.owner;
  if (!o) return "unknown";
  switch (type) {
    case "absentee":
      return o.isAbsentee === undefined ? "unknown" : o.isAbsentee ? "yes" : "no";
    case "entity":
      return o.isEntityOwner === undefined ? "unknown" : o.isEntityOwner ? "yes" : "no";
    case "out_of_state": {
      const state = (o.mailingState || "").trim().toUpperCase();
      if (!state) return "unknown";
      return state === "NC" ? "no" : "yes";
    }
    case "estate":
      return o.name ? (isEstateName(o.name) ? "yes" : "no") : "unknown";
  }
}

export type OwnerMatch = "match" | "no-match" | "unknown";

/**
 * Does the property's owner match ANY of the checked types? An empty list is
 * "no filter" and always matches. A row is "unknown" only when no checked type
 * says yes and at least one could not be evaluated.
 */
export function matchOwnerTypes(p: Property, types: readonly string[]): OwnerMatch {
  if (types.length === 0) return "match";
  let sawUnknown = false;
  for (const t of types) {
    const v = verdictFor(t as OwnerType, p);
    if (v === "yes") return "match";
    if (v === "unknown") sawUnknown = true;
  }
  return sawUnknown ? "unknown" : "no-match";
}
